import React from 'react'
import { Form, FormInstance, Input, Radio, Space } from 'antd'
import { DirectionKey, TypeKey } from '../types'

type TypeStepProps = {
  form: FormInstance
  value: TypeKey
  /** Current MATCH direction, re-applied to the form when switching back to MATCH. */
  directionKey: DirectionKey
  /** Notifies the host form so it can rebuild its steps for the chosen type. */
  onChange: (typeKey: TypeKey) => void
  /**
   * Types the user may choose from. When only one is allowed the picker is hidden
   * and the type is fixed — only the hidden carry-fields render, so the payload
   * still includes `type_key`/`direction_key`. Defaults to both.
   */
  allowedTypes?: TypeKey[]
}

const TYPE_LABEL: Record<TypeKey, string> = {
  DIRECT: 'Direct',
  MATCH: 'Match',
}

/**
 * First step of a MATCH-capable mapping (Color, Branch): pick DIRECT vs MATCH. The
 * `type_key`/`direction_key` enums are state-driven (the radios aren't Form-bound),
 * so they're kept in the payload via hidden inputs and persisted on every change.
 * DIRECT has no direction, so it's cleared; MATCH always carries one.
 *
 * When `allowedTypes` restricts the choice to a single type the picker is omitted
 * (only the hidden carry-fields render); the host fixes the type via its own state.
 */
export const TypeStep = ({ form, value, directionKey, onChange, allowedTypes = ['DIRECT', 'MATCH'] }: TypeStepProps) => {
  const handleChange = (typeKey: TypeKey) => {
    form.setFieldsValue({
      type_key: typeKey,
      direction_key: typeKey === 'DIRECT' ? null : directionKey,
    })
    onChange(typeKey)
  }

  return (
    <>
      <Form.Item name="type_key" hidden><Input /></Form.Item>
      <Form.Item name="direction_key" hidden><Input /></Form.Item>

      {allowedTypes.length > 1 && (
        <Form.Item
          label="Type"
          help={
            <Space direction="vertical" size={1}>
              <span><strong>Direct</strong>: an option of ours ↔ external value.</span>
              <span><strong>Match</strong>: evaluate an expression to decide the mapping.</span>
            </Space>
          }
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={value}
            onChange={e => handleChange(e.target.value)}
          >
            {allowedTypes.map(type => (
              <Radio.Button key={type} value={type}>{TYPE_LABEL[type]}</Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
      )}
    </>
  )
}
